<template>
  <div class="container">
    <div class="calendar">
      <div class="columns is-gapless is-mobile heading tw-mb-10">
        <div class="column is-6">
          <span class="title tw-text-left tw-uppercase">
            Events
          </span>
        </div>
        <div class="column is-6 tw-text-right" v-if="group.is_group_leader">
          <span class="">
            <b-button type="is-primary" @click="createEventToday()"
              >Create Event</b-button
            >
          </span>
        </div>
      </div>

      <div class="columns tw-mb-20">
        <div class="column tw-relative">
          <full-calendar
            ref="fullcalendar"
            class="group-calendar"
            :options="options"
          />
          <small class="tz"
            >Timezone:
            {{ calendarOptions.timeZone + ' (UTC' + calendar.utcOffset + ')' }}
          </small>
        </div>
      </div>

      <view-calendar-event
        ref="eventView"
        :event="event"
        @openEventForm="openEventForm('edit-event')"
      />
      <create-calendar-event
        ref="eventForm"
        :event="event"
        :action="calendarAction"
        @closed="refreshCalendar()"
        @editCategories="openEditCategories()"
        @confirmDeleteEvent="openConfirmDeleteEvent()"
      />
      <edit-calendar-categories
        ref="editCategories"
        :event="event"
        @closed="calendarAction ? $refs.eventForm.open() : ``"
      />
      <confirm-delete-event
        ref="eventDelete"
        :event="event"
        @closed="calendarAction ? $refs.eventForm.open() : refreshCalendar()"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import ViewCalendarEvent from '@/components/modals/ViewCalendarEvent'
import CreateCalendarEvent from '@/components/modals/CreateCalendarEvent'
import EditCalendarCategories from '@/components/modals/EditCalendarCategories'
import ConfirmDeleteEvent from '@/components/modals/ConfirmDeleteEvent'
import dayjs from 'dayjs'
import 'vue-swatches/dist/vue-swatches.css'

export default {
  name: 'Calendar',
  components: {
    FullCalendar,
    ViewCalendarEvent,
    ConfirmDeleteEvent,
    CreateCalendarEvent,
    EditCalendarCategories
  },
  props: {
    refresh: {
      type: Boolean,
      default: false
    },
    pageGroup: {
      type: String
    }
  },
  data() {
    return {
      isLoading: false,
      event: {
        id: null,
        date: '',
        type: ''
      },
      eventsJson: [],
      options: {
        plugins: [dayGridPlugin, interactionPlugin],
        views: {
          dayGrid: {
            dayMaxEventRows: 2
          }
        },
        eventClick: info => {
          this.event.id = parseInt(info.event._def.publicId)
          this.event.type = info.event._def.extendedProps.event_type
          this.$refs.eventView.open()
        },
        dateClick: async info => {
          this.event.date = info.date
          await this.checkDateValidity(info.date)
        }
      }
    }
  },
  async mounted() {
    this.options = { ...this.calendarOptions, ...this.options }
    await this.refreshCalendar()
  },
  watch: {
    refresh() {
      this.refreshCalendar()
    }
  },
  computed: {
    ...mapState({
      group: state => state.group.currentGroup,
      calendarEvents: state => state.group.calendar.events,
      calendarOptions: state => state.calendar.options,
      calendarAction: state => state.calendar.action,
      calendar: state => state.calendar
    })
  },
  methods: {
    ...mapActions({
      getCalendarEvents: 'group/getCalendarEvents',
      viewGroup: 'group/viewGroup'
    }),
    ...mapMutations(['setCalendarAction']),

    createEventToday() {
      let currMins = dayjs().minute()
      let setHours = dayjs()
      let setCurrMins = null

      switch (true) {
        case currMins >= 0 && currMins <= 5:
          setCurrMins = '5'
          break
        case currMins >= 6 && currMins <= 10:
          setCurrMins = '10'
          break
        case currMins >= 11 && currMins <= 15:
          setCurrMins = '15'
          break
        case currMins >= 16 && currMins <= 20:
          setCurrMins = '20'
          break
        case currMins >= 21 && currMins <= 25:
          setCurrMins = '25'
          break
        case currMins >= 26 && currMins <= 30:
          setCurrMins = '30'
          break
        case currMins >= 31 && currMins <= 35:
          setCurrMins = '35'
          break
        case currMins >= 36 && currMins <= 40:
          setCurrMins = '40'
          break
        case currMins >= 41 && currMins <= 45:
          setCurrMins = '45'
          break
        case currMins >= 46 && currMins <= 50:
          setCurrMins = '50'
          break
        case currMins >= 51 && currMins <= 55:
          setCurrMins = '55'
          break
        case currMins >= 56 && currMins <= 59:
          setCurrMins = '00'
          setHours = setHours.add(1, 'h')
          break
      }

      let currDate = setHours.minute(parseInt(setCurrMins)).valueOf()

      this.event.date = currDate
      this.checkAuth()
    },
    async checkDateValidity(date) {
      let selectedDate = dayjs(date)
      let todaysDate = dayjs()
      if (selectedDate < todaysDate) {
        return false
      }
      await this.checkAuth()
    },
    async checkAuth() {
      if (this.group.is_owner || this.group.is_group_leader) {
        await this.openEventForm('create-event')
      }
    },
    openConfirmDeleteEvent() {
      this.$refs.eventDelete.open()
    },
    openEditCategories() {
      this.$refs.editCategories.open()
    },
    async openEventForm(action) {
      this.setCalendarAction(action)
      this.$refs.eventForm.open()
    },
    convertDateTimeToTz() {
      this.eventsJson.forEach(event => {
        event.extendedProps.event_type === 'master'
          ? (event.color = '#00adcd')
          : ``

        if (event.startRecur && event.extendedProps.repeat_by !== `daily`) {
          event.startRecur = dayjs(event.startRecur)
            .tz(this.calendarOptions.timeZone)
            .format()
          event.endRecur = dayjs(event.endRecur)
            .tz(this.calendarOptions.timeZone)
            .format()
        } else if (
          event.startRecur &&
          event.extendedProps.repeat_by === `daily`
        ) {
          event.startRecur = dayjs(event.startRecur)
            .tz(this.calendarOptions.timeZone)
            .format()
          event.endRecur = dayjs(event.endRecur)
            .tz(this.calendarOptions.timeZone)
            .format()

          delete event.daysOfWeek
        } else {
          event.start = dayjs(event.start)
            .tz(this.calendarOptions.timeZone)
            .format()
        }
      })
    },
    refreshCalendar() {
      if (this.pageGroup !== this.group.slug) {
        this.resetCalendarSelected()
        this.getCalendarEvents().then(async () => {
          this.eventsJson = this.calendarEvents
          await this.convertDateTimeToTz()
          this.options.events = this.eventsJson
        })
      } else {
        this.eventsJson = []
        this.options.events = this.eventsJson
      }
    },
    resetCalendarSelected() {
      this.event = {
        id: null,
        date: '',
        type: ''
      }
    }
  }
}
</script>

<style></style>
